import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ButtonLinkProps {
    href: string
    children: ReactNode
    variant?: 'primary' | 'ghost'
    withArrow?: boolean
    className?: string
}

const variants = {
    primary: 'bg-gold text-ink shadow-soft hover:bg-[#dcbb4c] hover:shadow-glow-lg',
    ghost: 'bg-parchment/[0.04] text-parchment ring-1 ring-gold/25 hover:bg-gold/10 hover:text-gold hover:shadow-glow hover:ring-gold/50'
}

export const buttonLinkClasses = (variant: 'primary' | 'ghost' = 'primary', className?: string): string => cn(
    'group inline-flex items-center justify-center gap-3 rounded-sm px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.22em] transition-[background-color,box-shadow,color] duration-500 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-ink',
    variants[variant],
    className
)

const ButtonLink: FC<ButtonLinkProps> = ({
    href,
    children,
    variant = 'primary',
    withArrow = true,
    className
}): ReactNode => {
    return (
        <Link href={href} className={buttonLinkClasses(variant, className)}>
            {children}

            {withArrow && (
                <ArrowRight
                    aria-hidden='true'
                    className='size-4 transition-transform duration-500 ease-out group-hover:translate-x-1 motion-reduce:transition-none'
                />
            )}
        </Link>
    )
}

export default ButtonLink
